'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useState, useTransition } from 'react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input, Select } from '@/components/Input';
import { NeedsAuth } from '@/components/NeedsAuth';
import { PageTitle } from '@/components/PageTitle';
import { getAccessToken } from '@/lib/auth';
import { getApiUrl } from '@/lib/settings';
import { getClient } from '@/lib/api';
import { eur, shortDate } from '@/lib/fmt';
import type { components } from '@/lib/api.types';

type Tx = components['schemas']['TransactionOut'];
type CategorizeStats = components['schemas']['CategorizeStatsOut'];

const SOURCES = ['', 'revolut', 'paypal', 'novobanco'] as const;

/**
 * Transaction list with filters and inline editing of category + deductible.
 *
 * Filtering is server-side for the heavy fields (date range, source, category,
 * search needle). The deductible column is purely cosmetic on the wire — it
 * tells the dashboard's MTD spend calc how much of each row counts.
 *
 * The page also accepts deep-link query params (`from`, `to`, `source`,
 * `category`, `q`, `counts_toward_budget`) so the dashboard can open it
 * pre-filtered to "exactly what counts toward this month's budget".
 */
export default function TransactionsPage() {
  return (
    <NeedsAuth>
      <Suspense>
        <Inner />
      </Suspense>
    </NeedsAuth>
  );
}

function Inner() {
  const search = useSearchParams();
  const [from, setFrom] = useState<string>(() => search.get('from') || isoDaysAgo(30));
  const [to, setTo] = useState<string>(() => search.get('to') || isoToday());
  const [source, setSource] = useState<string>(() => search.get('source') || '');
  const [category, setCategory] = useState<string>(() => search.get('category') || '');
  const [q, setQ] = useState<string>(() => search.get('q') || '');
  const [countsTowardBudget, setCountsTowardBudget] = useState<boolean>(
    () => parseBoolParam(search.get('counts_toward_budget')),
  );
  // Debounced version of `q` so we don't fire a request on every keystroke.
  const debouncedQ = useDebounced(q, 300);

  const [rows, setRows] = useState<Tx[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingDeductible, setEditingDeductible] = useState<string | null>(null);
  // Bulk-edit selection. Cleared whenever the filter set changes — if the
  // user changes filters with rows selected, those rows might not even be on
  // screen anymore, so carrying the selection over would be confusing.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState<
    null | 'category' | 'deductible-zero' | 'deductible-reset' | 'deductible-set' | 'delete'
  >(null);
  const [tick, setTick] = useState(0);
  const [categorizing, setCategorizing] = useState(false);
  const [categorizeStats, setCategorizeStats] = useState<CategorizeStats | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const uncategorizedCount = rows.filter((r) => !r.category).length;

  const summary = useMemo(() => {
    let credits = 0;
    let debits = 0;
    let deductible = 0;
    for (const tx of rows) {
      const n = Number(tx.amount);
      if (Number.isNaN(n)) continue;
      if (tx.direction === 'credit') {
        credits += n;
      } else {
        debits += n;
        const d = tx.deductible !== null && tx.deductible !== undefined ? Number(tx.deductible) : n;
        if (!Number.isNaN(d)) deductible += d;
      }
    }
    return {
      count: rows.length,
      credits,
      debits,
      deductible,
      net: credits - debits,
      capped: rows.length === 500,
    };
  }, [rows]);

  /**
   * Pull from Enable Banking over the currently visible date range, then
   * re-fetch the list. Idempotent — re-running over an already-imported
   * window upserts on the same `(source, external_id)` hash.
   */
  async function runRefresh() {
    const client = getClient();
    if (!client) return;
    if (!from || !to) {
      setRefreshError('Set a from/to date in the filters first.');
      return;
    }
    setRefreshing(true);
    setRefreshError(null);
    try {
      const { error, response } = await client.POST('/api/sync/novobanco/range', {
        params: { query: { date_from: from, date_to: to } },
      });
      if (error) {
        setRefreshError(`${response.status}: ${JSON.stringify(error)}`);
      } else {
        setTick((t) => t + 1);
      }
    } finally {
      setRefreshing(false);
    }
  }

  async function runCategorize() {
    const client = getClient();
    if (!client) return;
    setCategorizing(true);
    setError(null);
    setCategorizeStats(null);
    try {
      const { data, error } = await client.POST('/api/transactions/categorize', {});
      if (error) setError(JSON.stringify(error));
      else {
        setCategorizeStats(data ?? null);
        setTick((t) => t + 1);
      }
    } finally {
      setCategorizing(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const client = getClient();
    if (!client) return;
    void (async () => {
      if (!cancelled) setError(null);
      const { data, error } = await client.GET('/api/transactions', {
        params: {
          query: {
            from_date: from || undefined,
            to_date: to || undefined,
            source: source || undefined,
            category: category || undefined,
            q: debouncedQ || undefined,
            counts_toward_budget: countsTowardBudget || undefined,
            limit: 500,
          },
        },
      });
      if (cancelled) return;
      if (error) setError(JSON.stringify(error));
      else setRows(data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [from, to, source, category, debouncedQ, countsTowardBudget, tick]);

  // Drop selection whenever the filter set changes — selected IDs from a
  // previous view aren't necessarily in the new row set, and acting on
  // off-screen rows would be confusing. The post-bulk `setTick` refetch is
  // intentionally NOT in here (the bulk handlers clear `selected` themselves
  // before bumping the tick).
  useEffect(() => {
    setSelected(new Set());
  }, [from, to, source, category, debouncedQ, countsTowardBudget]);

  // --- Bulk actions -------------------------------------------------------
  //
  // The typed openapi-fetch client doesn't know about /transactions/bulk yet
  // (api.types.ts is regenerated against the running backend). We hit the
  // endpoints with bare fetch + the existing access token; once gen:api runs
  // these can be replaced with `client.POST('/api/transactions/bulk/category', …)`.
  async function bulkRequest(
    path: string,
    method: 'POST' | 'PATCH',
    body: unknown,
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    const token = getAccessToken();
    if (!token) return { ok: false, error: 'not authenticated' };
    const res = await fetch(`${getApiUrl()}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, error: `${res.status}: ${text}` };
    }
    return { ok: true };
  }

  async function bulkRecategorize(cat: string) {
    if (!selected.size || !cat.trim()) return;
    setBulkBusy('category');
    setError(null);
    const ids = Array.from(selected);
    const result = await bulkRequest('/api/transactions/bulk/category', 'POST', {
      ids,
      category: cat.trim(),
      learn: true,
    });
    setBulkBusy(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSelected(new Set());
    setTick((t) => t + 1);
  }

  async function bulkDeductible(action: 'zero' | 'reset' | 'set', amount?: number) {
    if (!selected.size) return;
    if (action === 'set' && (amount === undefined || Number.isNaN(amount) || amount < 0)) return;
    setBulkBusy(
      action === 'zero'
        ? 'deductible-zero'
        : action === 'reset'
          ? 'deductible-reset'
          : 'deductible-set',
    );
    setError(null);
    const ids = Array.from(selected);
    const body =
      action === 'zero'
        ? { ids, deductible: 0, clear_deductible: false }
        : action === 'reset'
          ? { ids, clear_deductible: true }
          : { ids, deductible: amount, clear_deductible: false };
    const result = await bulkRequest('/api/transactions/bulk', 'PATCH', body);
    setBulkBusy(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSelected(new Set());
    setTick((t) => t + 1);
  }

  async function bulkDelete() {
    if (!selected.size) return;
    // Native confirm is intentional: destructive bulk actions deserve a
    // pause, and a one-off `<Dialog>` for this feels heavier than the
    // action warrants. Rows reappear on next sync (idempotent upstream
    // import), so the blast radius is "this view, until you resync".
    const ok = window.confirm(
      `Delete ${selected.size} transaction${selected.size === 1 ? '' : 's'}? They'll be recreated on the next sync if the upstream still has them.`,
    );
    if (!ok) return;
    setBulkBusy('delete');
    setError(null);
    const ids = Array.from(selected);
    const result = await bulkRequest('/api/transactions/bulk/delete', 'POST', { ids });
    setBulkBusy(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSelected(new Set());
    setTick((t) => t + 1);
  }

  // Header checkbox helpers — select-all flips between "everything in view"
  // and "nothing". Indeterminate state surfaces when some-but-not-all are
  // ticked, which is the only visual cue we have without dedicated checkbox
  // chrome (we render a CSS-only square so the tactile feel matches Pill).
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;
  function toggleAll() {
    if (allSelected || someSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((r) => r.id)));
    }
  }
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageTitle
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={runRefresh}
              loading={refreshing}
              disabled={!from || !to}
              title={`Pull from Enable Banking over ${from} → ${to}`}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={runCategorize}
              loading={categorizing}
              title={
                uncategorizedCount > 0
                  ? `${uncategorizedCount} uncategorized in this view; runs over ALL uncategorized rows in the DB`
                  : 'Runs over every uncategorized row in the DB'
              }
            >
              Categorize uncategorized
            </Button>
          </>
        }
      >
        Transactions
      </PageTitle>

      <Card title="Filters">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            From
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            To
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            Source
            <Select value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s || 'all'}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            Category
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="exact match"
              className="font-mono !text-xs"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            Search
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="merchant / description"
            />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={countsTowardBudget}
            onClick={() => setCountsTowardBudget((v) => !v)}
            className={
              'pressable rounded-full px-3 py-1 text-xs ' +
              (countsTowardBudget
                ? 'bg-hero-wash text-fg-primary shadow-sm'
                : 'bg-sunken text-fg-muted hover:bg-raised')
            }
            title="Limit to debits that count toward the dashboard's monthly budget — excludes income, transfers, savings & cash withdrawals"
          >
            {countsTowardBudget ? '✓ ' : ''}counts toward budget
          </button>
        </div>
      </Card>

      {error && (
        <Card>
          <p className="text-sm text-negative">Error: {error}</p>
        </Card>
      )}

      {refreshError && (
        <Card>
          <p className="text-sm text-negative">Refresh failed: {refreshError}</p>
        </Card>
      )}

      {categorizeStats && (
        <Card>
          <p className="text-sm text-fg-primary">
            Categorized <b>{categorizeStats.examined}</b> rows ·{' '}
            <span className="text-positive">{categorizeStats.by_rule} by rule</span> ·{' '}
            <span style={{ color: 'var(--hero-start)' }}>{categorizeStats.by_ai} by AI</span>
            {categorizeStats.skipped_no_ai > 0 && (
              <>
                {' '}
                · <span className="text-negative">{categorizeStats.skipped_no_ai} skipped (no AI key / CLI)</span>
              </>
            )}
            {categorizeStats.failed > 0 && (
              <>
                {' '}
                · <span className="text-negative">{categorizeStats.failed} failed</span>
              </>
            )}
          </p>
        </Card>
      )}

      <Card hint={`${rows.length} row${rows.length === 1 ? '' : 's'}`}>
        {rows.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
            <span>
              {summary.count} transaction{summary.count === 1 ? '' : 's'}
            </span>
            <span aria-hidden>·</span>
            <span>
              <span className="tnum font-mono text-positive">+{eur(summary.credits)}</span> in
            </span>
            <span aria-hidden>·</span>
            <span>
              <span className="tnum font-mono text-fg-primary">−{eur(summary.debits)}</span> out
            </span>
            <span aria-hidden>·</span>
            <span>
              <span className="tnum font-mono text-fg-primary">{eur(summary.deductible)}</span>{' '}
              deductible
            </span>
            <span aria-hidden>·</span>
            <span>
              net{' '}
              <span
                className={
                  'tnum font-mono ' + (summary.net >= 0 ? 'text-positive' : 'text-negative')
                }
              >
                {summary.net >= 0 ? '+' : '−'}
                {eur(Math.abs(summary.net))}
              </span>
            </span>
            {summary.capped && (
              <span>· showing first 500 — narrow your search for a complete total</span>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-fg-muted">
              <tr className="border-b border-hairline">
                <th className="w-8 py-2 pr-2 font-medium">
                  <SelectAllBox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onToggle={toggleAll}
                    disabled={rows.length === 0}
                    label="Select all visible transactions"
                  />
                </th>
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Source</th>
                <th className="py-2 pr-4 font-medium">Description</th>
                <th className="py-2 pr-4 text-right font-medium">Amount</th>
                <th className="py-2 pr-4 text-right font-medium">Deductible</th>
                <th className="py-2 pr-4 font-medium">Category</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-fg-muted">
                    No transactions match these filters.
                  </td>
                </tr>
              )}
              {rows.map((tx) => (
                <tr
                  key={tx.id}
                  className={
                    'border-b border-hairline hover:bg-raised ' +
                    (selected.has(tx.id) ? 'bg-hero-wash' : '')
                  }
                >
                  <td className="py-2 pr-2">
                    <SelectBox
                      checked={selected.has(tx.id)}
                      onToggle={() => toggleOne(tx.id)}
                      label={`Select transaction ${tx.merchant ?? tx.description}`}
                    />
                  </td>
                  <td className="tnum whitespace-nowrap py-2 pr-4">{shortDate(tx.booked_at)}</td>
                  <td className="py-2 pr-4 text-fg-muted">{tx.source}</td>
                  <td className="py-2 pr-4">
                    <div className="font-medium text-fg-primary">{tx.merchant ?? tx.description}</div>
                    {tx.merchant && (
                      <div className="line-clamp-1 text-xs text-fg-muted">{tx.description}</div>
                    )}
                  </td>
                  <td
                    className={
                      'tnum py-2 pr-4 text-right font-mono ' +
                      (tx.direction === 'credit' ? 'text-positive' : 'text-fg-primary')
                    }
                  >
                    {tx.direction === 'credit' ? '+' : '−'}
                    {eur(tx.amount)}
                  </td>
                  <td className="tnum py-2 pr-4 text-right font-mono">
                    <DeductibleCell
                      tx={tx}
                      editing={editingDeductible === tx.id}
                      onStart={() => setEditingDeductible(tx.id)}
                      onCancel={() => setEditingDeductible(null)}
                      onSaved={() => {
                        setEditingDeductible(null);
                        setTick((t) => t + 1);
                      }}
                      onError={(msg) => setError(msg)}
                    />
                  </td>
                  <td className="py-2 pr-4">
                    {editingCategory === tx.id ? (
                      <RecategorizeInput
                        currentCategory={tx.category ?? ''}
                        onCancel={() => setEditingCategory(null)}
                        onSave={async (cat) => {
                          const client = getClient();
                          if (!client) return;
                          const { error } = await client.POST('/api/transactions/{transaction_id}/category', {
                            params: { path: { transaction_id: tx.id } },
                            body: { category: cat, learn: true },
                          });
                          if (error) {
                            setError(JSON.stringify(error));
                          } else {
                            setEditingCategory(null);
                            setTick((t) => t + 1);
                          }
                        }}
                      />
                    ) : (
                      <button
                        onClick={() => setEditingCategory(tx.id)}
                        className="pressable rounded-full bg-sunken px-2 py-0.5 text-left text-xs text-fg-primary hover:bg-raised"
                      >
                        {tx.category ?? <span className="text-fg-muted">uncategorized</span>}
                      </button>
                    )}
                    {tx.category_source && (
                      <div className="mt-1 text-[10px] text-fg-muted">{tx.category_source}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          busy={bulkBusy}
          onRecategorize={bulkRecategorize}
          onSetDeductible={(amount) => bulkDeductible('set', amount)}
          onExcludeFromBudget={() => bulkDeductible('zero')}
          onIncludeInBudget={() => bulkDeductible('reset')}
          onDelete={bulkDelete}
          onClear={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}

/**
 * Sticky bottom action bar — surfaces once ≥1 row is selected. Single hero
 * action (apply category) sits on the right per DESIGN.md §8.3 "one primary
 * per surface"; budget/deductible chips are secondary; the destructive
 * Delete is `destructive` variant per DESIGN.md §8.3 and confirms via
 * `window.confirm` before firing.
 *
 * Two rows on mobile, one on wide viewports (`md:flex-nowrap`) — the
 * category input grows to fill, the chips and trailing actions wrap below.
 */
function BulkActionBar({
  count,
  busy,
  onRecategorize,
  onSetDeductible,
  onExcludeFromBudget,
  onIncludeInBudget,
  onDelete,
  onClear,
}: {
  count: number;
  busy:
    | null
    | 'category'
    | 'deductible-zero'
    | 'deductible-reset'
    | 'deductible-set'
    | 'delete';
  onRecategorize: (category: string) => void | Promise<void>;
  onSetDeductible: (amount: number) => void | Promise<void>;
  onExcludeFromBudget: () => void | Promise<void>;
  onIncludeInBudget: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  onClear: () => void;
}) {
  const [category, setCategory] = useState('');
  const [deductibleAmount, setDeductibleAmount] = useState('');
  const parsedDeductible = Number(deductibleAmount);
  const deductibleValid =
    deductibleAmount.trim() !== '' && !Number.isNaN(parsedDeductible) && parsedDeductible >= 0;
  return (
    <div className="sticky bottom-4 z-10 mx-auto w-full max-w-4xl">
      <div className="flex flex-wrap items-center gap-3 rounded-3xl bg-surface px-4 py-3 shadow-lg ring-1 ring-hairline md:flex-nowrap">
        <span className="text-sm text-fg-primary">
          <b className="font-mono tnum">{count}</b> selected
        </span>

        <form
          className="flex min-w-[14rem] flex-1 items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (!category.trim()) return;
            void onRecategorize(category);
          }}
        >
          <Input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Food & Drink/Coffee"
            className="!flex-1 !py-1 font-mono !text-xs"
            aria-label="Apply category to selected"
          />
          <Button
            variant="primary"
            size="sm"
            type="submit"
            loading={busy === 'category'}
            disabled={busy !== null || !category.trim()}
          >
            Apply
          </Button>
        </form>

        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (!deductibleValid) return;
            void onSetDeductible(parsedDeductible);
          }}
        >
          <Input
            type="number"
            min={0}
            step="0.01"
            value={deductibleAmount}
            onChange={(e) => setDeductibleAmount(e.target.value)}
            placeholder="€ deductible"
            className="!w-28 !py-1 text-right font-mono !text-xs"
            aria-label="Set deductible amount on selected"
          />
          <Button
            variant="secondary"
            size="sm"
            type="submit"
            loading={busy === 'deductible-set'}
            disabled={busy !== null || !deductibleValid}
            title="Apply this deductible to every selected debit"
          >
            Set
          </Button>
        </form>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => void onExcludeFromBudget()}
          loading={busy === 'deductible-zero'}
          disabled={busy !== null}
          title="Mark selected debits as not counting toward this month's budget (deductible → 0)"
        >
          Exclude from budget
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void onIncludeInBudget()}
          loading={busy === 'deductible-reset'}
          disabled={busy !== null}
          title="Include in budget at the full amount (clears any deductible override)"
        >
          Include
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => void onDelete()}
          loading={busy === 'delete'}
          disabled={busy !== null}
          title="Permanently delete the selected rows (re-imported on next sync)"
        >
          Delete
        </Button>
        <Button variant="ghost" size="sm" onClick={onClear} disabled={busy !== null}>
          ×
        </Button>
      </div>
    </div>
  );
}

/**
 * Compact tactile checkbox — DESIGN.md doesn't ship a checkbox primitive, so
 * this is a one-shot extension styled to match Pill / Input: rounded square,
 * `bg/sunken` empty, `hero/start` filled with a checkmark glyph. Press
 * scales 0.97 via the global `.pressable` utility.
 */
function SelectBox({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onToggle}
      className={
        'pressable inline-flex h-5 w-5 items-center justify-center rounded-md ' +
        (checked
          ? 'bg-[var(--hero-start)] text-fg-inverse shadow-sm'
          : 'bg-sunken text-fg-muted hover:bg-raised')
      }
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M3 8.5l3 3 7-7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

/**
 * Header checkbox — adds the "indeterminate" mid-state (some-but-not-all
 * ticked) that a single row can't have. Rendered as a horizontal bar so the
 * three states (empty / partial / full) read at a glance.
 */
function SelectAllBox({
  checked,
  indeterminate,
  onToggle,
  disabled,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  onToggle: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={label}
      onClick={onToggle}
      disabled={disabled}
      className={
        'pressable inline-flex h-5 w-5 items-center justify-center rounded-md disabled:opacity-40 ' +
        (checked || indeterminate
          ? 'bg-[var(--hero-start)] text-fg-inverse shadow-sm'
          : 'bg-sunken text-fg-muted hover:bg-raised')
      }
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M3 8.5l3 3 7-7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {indeterminate && !checked && (
        <span className="block h-0.5 w-2.5 rounded-full bg-current" />
      )}
    </button>
  );
}

function RecategorizeInput({
  currentCategory,
  onCancel,
  onSave,
}: {
  currentCategory: string;
  onCancel: () => void;
  onSave: (category: string) => void | Promise<void>;
}) {
  const [val, setVal] = useState(currentCategory);
  const [pending, startTransition] = useTransition();
  return (
    <form
      className="flex gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (!val.trim()) return;
        startTransition(() => {
          void onSave(val.trim());
        });
      }}
    >
      <Input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Food & Drink/Coffee"
        className="!w-48 !py-0.5 font-mono !text-xs"
      />
      <Button variant="primary" size="sm" type="submit" loading={pending}>
        save
      </Button>
      <Button variant="ghost" size="sm" onClick={onCancel}>
        ×
      </Button>
    </form>
  );
}

/**
 * Editable cell: shows the current deductible, defaulting to the row amount
 * when no override is set. Click to edit, set 0 for a fully-offset
 * transaction or any other partial amount.
 */
function DeductibleCell({
  tx,
  editing,
  onStart,
  onCancel,
  onSaved,
  onError,
}: {
  tx: Tx;
  editing: boolean;
  onStart: () => void;
  onCancel: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const isCredit = tx.direction === 'credit';
  // Credits don't count toward spend; deductible is meaningless. Render a dash.
  if (isCredit) {
    return <span className="text-fg-muted">—</span>;
  }

  const isOverride = tx.deductible !== null && tx.deductible !== undefined;
  const effective = isOverride ? Number(tx.deductible) : Number(tx.amount);

  if (!editing) {
    return (
      <button
        onClick={onStart}
        className={
          'pressable rounded-full px-2 py-0.5 text-right ' +
          (isOverride
            ? 'bg-hero-wash text-fg-primary'
            : 'bg-sunken text-fg-muted hover:bg-raised')
        }
        title={isOverride ? 'override — click to edit' : 'full amount — click to override'}
      >
        {eur(effective)}
      </button>
    );
  }

  return (
    <DeductibleEdit
      transactionId={tx.id}
      initial={effective}
      isOverride={isOverride}
      onCancel={onCancel}
      onSaved={onSaved}
      onError={onError}
    />
  );
}

function DeductibleEdit({
  transactionId,
  initial,
  isOverride,
  onCancel,
  onSaved,
  onError,
}: {
  transactionId: string;
  initial: number;
  isOverride: boolean;
  onCancel: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [val, setVal] = useState(initial.toFixed(2));
  const [pending, startTransition] = useTransition();

  function persist(action: 'save' | 'clear') {
    startTransition(async () => {
      const client = getClient();
      if (!client) return;
      const body =
        action === 'clear'
          ? { clear_deductible: true, deductible: null }
          : { clear_deductible: false, deductible: Number(val) };
      const { error } = await client.PATCH('/api/transactions/{transaction_id}', {
        params: { path: { transaction_id: transactionId } },
        body,
      });
      if (error) {
        onError(JSON.stringify(error));
      } else {
        onSaved();
      }
    });
  }

  return (
    <form
      className="flex items-center justify-end gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        persist('save');
      }}
    >
      <Input
        autoFocus
        type="number"
        min={0}
        step="0.01"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="!w-24 !py-0.5 text-right font-mono !text-xs"
      />
      <Button variant="primary" size="sm" type="submit" loading={pending}>
        save
      </Button>
      {isOverride && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => persist('clear')}
          title="reset to full amount"
        >
          reset
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={onCancel}>
        ×
      </Button>
    </form>
  );
}

// --- helpers ----------------------------------------------------------------

function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function parseBoolParam(v: string | null): boolean {
  if (v === null) return false;
  const norm = v.trim().toLowerCase();
  return norm === '1' || norm === 'true' || norm === 'yes' || norm === 'on';
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}
